import { useCallback } from 'react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import type { ResolvedCommentsQueryType } from '@confluence/comments-panel-queries';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';

import { getResolvedAnnotationState, updateResolvedComments } from '../helper/commentHelpers';

const RESOLVE = 'resolve';

const useSyncResolvedEditorComments = () => {
	const { unresolvedInlineComments } = useInlineCommentsState();
	const editorAnnotationEventEmitter = getEditorAnnotationEventEmitter();

	return useCallback(() => {
		unresolvedInlineComments.forEach((comment) =>
			editorAnnotationEventEmitter.emit(RESOLVE, comment),
		);
	}, [editorAnnotationEventEmitter, unresolvedInlineComments]);
};

const useSyncResolvedRendererComments = () => {
	const { unresolvedInlineComments } = useInlineCommentsState();
	const rendererAnnotationEventEmitter = getRendererAnnotationEventEmitter();

	return useCallback(() => {
		const resolvedAnnotationState = getResolvedAnnotationState(unresolvedInlineComments);
		rendererAnnotationEventEmitter.emit(
			AnnotationUpdateEvent.SET_ANNOTATION_STATE,
			resolvedAnnotationState,
		);
	}, [rendererAnnotationEventEmitter, unresolvedInlineComments]);
};

export const useSyncResolvedComments = () => {
	const pageMode = useGetPageMode();
	const [{ commentsDataMap }, { setCommentsDataMap }] = useCommentsData();
	const syncResolvedEditorComments = useSyncResolvedEditorComments();
	const syncResolvedRendererComments = useSyncResolvedRendererComments();

	return useCallback(
		(resolvedComments: ResolvedCommentsQueryType) => {
			setCommentsDataMap(updateResolvedComments(resolvedComments, commentsDataMap));

			if (pageMode === PageMode.VIEW) {
				syncResolvedRendererComments();
			} else {
				syncResolvedEditorComments();
			}
		},
		[
			commentsDataMap,
			setCommentsDataMap,
			pageMode,
			syncResolvedRendererComments,
			syncResolvedEditorComments,
		],
	);
};
